import { EXTRA_PROPS, type Editor } from './editor';
import { asNotEditing, cleanPageName, pageSizeProblem, guidesProblem, APP_FMT, hasDrawMarks, hasTables, hasTextFx, joinPages, movedIndex, namesKnownAfter, noGuides, MAX_PAGE_NAME, MAX_PAGES, mixedSizes, sizesOf, splitPages, type PageGuides, type PageSize } from './pageFormat';
import { checkOverlays, checkVideo, upgradeVideo, type VideoProject } from '../video/project';

/**
 * the video project inside a design's JSON (undefined for a design), as v2; a broken one — or text/pictures with
 * unusable timing on its page — stops the load with its reason (the same checks as editing and the server)
 */
function videoIn(meta: Record<string, unknown>, page1: string): VideoProject | undefined {
  if (meta.video === undefined) return undefined;
  const c = checkVideo(meta.video);
  if (!c.ok) throw new Error('ข้อมูลวิดีโอในงานนี้เสีย: ' + c.error);
  const why = checkOverlays(JSON.parse(page1));
  if (why) throw new Error('ข้อมูลวิดีโอในงานนี้เสีย: ' + why);
  return upgradeVideo(meta.video);
}

const MAX_STEPS = 60;
/** undo steps hold the whole design (every page); drop the oldest ones above this many characters */
const MAX_HISTORY_CHARS = 40_000_000;

interface Step { json: string; page: number }

/**
 * The design's pages and undo/redo.
 *
 * Only the page being edited is on the canvas; the other pages are kept as JSON text (src/editor/pageFormat.ts).
 * Each page has its own size and print bleed (`sizes`); the editor's doc.w/h/bleedMm are always the open page's.
 * Undo/redo snapshots hold the whole design, so adding, deleting or moving a page is undone like any edit,
 * and undo goes back to the page where the change was made.
 */
export function createHistory(ed: Editor, hooks: {
  onChange: () => void;
  /** after any page is put on the canvas (open, undo/redo, switching page) */
  onRestored: () => void;
  /** a page opened with fewer objects than it has (usually a picture that did not load): saving must stop */
  onIncomplete: (reason: string) => void;
  /** something could not be done but the open work is unchanged (shown to the person, saving goes on) */
  onProblem: (reason: string) => void;
}) {
  let stack: Step[] = [];
  let i = -1;
  let locked = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  /*
   * While a page is loading, Fabric keeps the previous page's objects on the canvas until every picture has
   * arrived, but `cur` already names the new page. So nothing may read the canvas into pages[cur] then, and
   * a second load waits for the first (a stale load finishing last would put the wrong page on the canvas).
   */
  let loading = false;
  const afterLoad: Array<() => void> = [];

  let pages: string[] = ['[]'];        // each page's objects as JSON text; pages[cur] is refreshed from the canvas when needed
  let cur = 0;
  let meta: Record<string, unknown> = { version: '5.3.0' };
  const sizeNow = (): PageSize => ({ w: ed.doc.w, h: ed.doc.h, bleedMm: ed.doc.bleedMm });
  let sizes: PageSize[] = [sizeNow()];  // each page's size and bleed; sizes[cur] is what the editor shows
  let names: string[] = [''];            // each page's name ('' = none)
  /**
   * whether this app knows the design's page names: the JSON it opened had them, or a name was set here. Only then are
   * they written — a design saved without them by an older app (or left on a device by one) must not be saved as
   * "no names" over names set elsewhere: the server refuses that save (pageFormat.ts, worker PUT)
   */
  let namesKnown = true;
  /** the same for the drawing tools' data (notes, arrow heads, pen strokes — pageFormat.ts hasDrawMarks) */
  let drawKnown = true;
  /** the same for เอฟเฟกต์ข้อความ (pageFormat.ts hasTextFx, D1 has_text_fx 0020) */
  let fxKnown = true;
  /** the same for ตาราง (pageFormat.ts hasTables, D1 has_tables 0027) */
  let tablesKnown = true;
  /** each page's guides (เส้นไกด์ from the rulers, src/ui/rulers.ts); guides[cur] is what the editor shows */
  let guides: PageGuides[] = [noGuides()];
  /** the same as namesKnown, for guides: written only once the app knows them (the JSON had them, or one was added) */
  let guidesKnown = true;
  const copyGuides = (g: PageGuides): PageGuides => ({ x: g.x.slice(), y: g.y.slice() });

  /** the page on the canvas → pages[cur] (and its size); returns the canvas JSON without its objects (version, background) */
  function readCanvas(): Record<string, unknown> {
    const { objects, ...rest } = asNotEditing(ed.canvas.getObjects(), () => ed.canvas.toJSON(EXTRA_PROPS)) as unknown as Record<string, unknown>;
    // while loading, pages[cur] and sizes[cur] already hold the incoming page
    if (!loading) { pages[cur] = JSON.stringify(objects ?? []); sizes[cur] = sizeNow(); }
    return rest;
  }

  /**
   * the whole design JSON; page 1's size in `size` and bleed in `print`, the other pages' on their own entries
   * (pageFormat.ts), a video project in `video` (Fabric ignores extra keys when loading)
   */
  function snapshot(): string {
    const m: Record<string, unknown> = { ...readCanvas() };
    m.size = { w: Math.round(sizes[0].w), h: Math.round(sizes[0].h) };
    if (sizes[0].bleedMm !== undefined) m.print = { bleedMm: sizes[0].bleedMm };
    if (ed.doc.video) m.video = ed.doc.video;
    if (!drawKnown && pages.some(hasDrawMarks)) drawKnown = true;
    if (drawKnown) m.draw = 1;
    if (!fxKnown && pages.some(hasTextFx)) fxKnown = true;
    if (fxKnown) m.textfx = 1;
    if (!tablesKnown && pages.some(hasTables)) tablesKnown = true;
    if (tablesKnown) m.tables = 1;
    // เลขรุ่นของงาน (0028): what this app knows, always (not the work's — undo never lowers it)
    m.fmt = APP_FMT;
    meta = m;
    return joinPages(m, pages, sizes, namesKnown ? names : null, guidesKnown ? guides : null);
  }

  /** the editor shows page `k`'s size and bleed (the view is fitted again only when they change) */
  function applySize(k: number) {
    const s = sizes[k];
    if (s.w !== ed.doc.w || s.h !== ed.doc.h) ed.setDocSize(s.w, s.h);
    ed.setBleed(s.bleedMm);
  }

  function record() {
    if (loading) return;
    const s = snapshot();
    if (stack[i]?.json === s) return;
    stack = stack.slice(0, i + 1);
    stack.push({ json: s, page: cur });
    let size = stack.reduce((n, x) => n + x.json.length, 0);
    while (stack.length > 2 && (stack.length > MAX_STEPS || size > MAX_HISTORY_CHARS)) size -= stack.shift()!.json.length;
    i = stack.length - 1;
    hooks.onChange();
  }

  function push() {
    if (locked) return;
    clearTimeout(timer);
    timer = setTimeout(() => { timer = undefined; record(); }, 250);
  }

  /** record an edit that is still waiting for its 250 ms pause, before the page changes under it */
  function flush() {
    if (locked || timer === undefined) return;
    clearTimeout(timer); timer = undefined;
    record();
  }

  /** put page `index` on the canvas, keeping the artboard at the bottom */
  function showPage(index: number, done?: () => void) {
    locked = true; loading = true;
    cur = Math.max(0, Math.min(index, pages.length - 1));
    const objects = JSON.parse(pages[cur]) as unknown[];
    ed.canvas.discardActiveObject();
    applySize(cur);
    ed.setPageGuides(guides[cur]);
    ed.canvas.loadFromJSON({ version: meta.version ?? '5.3.0', objects }, () => {
      ed.canvas.insertAt(ed.board, 0, false);
      ed.paint();
      locked = false; loading = false;
      ed.canvas.requestRenderAll();
      const got = ed.userObjects().length;
      if (got < objects.length) {
        hooks.onIncomplete(`หน้า ${cur + 1} เปิดได้ ${got} จาก ${objects.length} ชิ้น (มักเป็นรูปที่โหลดไม่ได้ เช่น ตอนไม่มีอินเทอร์เน็ต) ระบบจึงหยุดบันทึกเพื่อไม่ให้ทับงานเดิม เปิดหน้าเว็บใหม่อีกครั้งเมื่อออนไลน์`);
      }
      hooks.onRestored();
      done?.();
      afterLoad.splice(0).forEach(fn => fn());
    });
  }

  /**
   * replace the whole design with a snapshot (opening a design, undo/redo), on page `page`.
   * A broken design throws with a Thai reason; when the load had to wait for another one, that reason goes to
   * `failed` (or, without it, stops saving through onIncomplete) instead of being lost inside Fabric's callback.
   */
  function load(json: string, done?: () => void, page = 0, failed?: (e: unknown) => void, fallback?: PageSize, undoStep = false) {
    // page 1's size when the JSON has none (an old design): the opener's width × height, taken now — a load that
    // waits below runs later, when ed.doc may already hold the next design's size
    const one = fallback ?? { w: ed.doc.w, h: ed.doc.h };
    if (loading) {
      afterLoad.push(() => {
        try { load(json, done, page, failed, one, undoStep); }
        catch (e) {
          if (failed) failed(e);
          else hooks.onIncomplete('เปิดงานไม่ได้: ' + (e instanceof Error ? e.message : String(e)) + ' ระบบจึงหยุดบันทึกเพื่อไม่ให้ทับงานเดิม');
        }
      });
      return;
    }
    clearTimeout(timer); timer = undefined;
    const split = splitPages(json);
    const video = videoIn(split.meta, split.pages[0]);   // throws with a Thai reason before anything changes
    // page 1 without a size in the JSON (a design from before page sizes): the design's width × height, set by the opener
    const all = sizesOf(split, { w: one.w, h: one.h, bleedMm: undefined });
    pages = split.pages; meta = split.meta; sizes = all;
    names = split.names ?? pages.map(() => '');
    namesKnown = namesKnownAfter(namesKnown, split.names !== null, undoStep);
    guides = split.guides ?? pages.map(noGuides);
    guidesKnown = namesKnownAfter(guidesKnown, split.guides !== null, undoStep);
    drawKnown = namesKnownAfter(drawKnown, split.meta.draw === 1, undoStep);
    fxKnown = namesKnownAfter(fxKnown, split.meta.textfx === 1, undoStep);
    tablesKnown = namesKnownAfter(tablesKnown, split.meta.tables === 1, undoStep);
    ed.doc.video = video;
    showPage(page, done);
  }

  function go(step: number) {
    if (locked) return;
    flush();                       // an edit still in its 250 ms pause is a step of its own
    const j = i + step;
    if (j < 0 || j >= stack.length) return;
    // show the page where the change happened: undo → the page of the step being undone, redo → of the step redone
    const page = step < 0 ? stack[i].page : stack[j].page;
    const from = i;
    i = j;
    try { load(stack[j].json, hooks.onChange, page, undefined, undefined, true); }
    catch (e) {
      // a step that cannot be opened again: stay where we were and say why
      i = from;
      hooks.onProblem('เลิกทำ/ทำซ้ำไม่ได้: ' + (e instanceof Error ? e.message : String(e)) + ' งานบนจอยังเหมือนเดิม');
    }
  }

  /** start a fresh history from the current design (does not count as an edit) */
  function reset() { clearTimeout(timer); timer = undefined; stack = [{ json: snapshot(), page: cur }]; i = 0; }

  /** run fn without recording history (used when building the start state) */
  function silently(fn: () => void) { locked = true; try { fn(); } finally { locked = false; } }

  /* ---------- pages ---------- */

  /** Returns a Thai reason when the page operation cannot be done now. */
  const busy = () => locked ? 'กำลังเปิดหน้าอยู่ รอสักครู่แล้วลองใหม่' : null;

  /** open page `index`; `done` runs once it is on the canvas (right away if it already is) */
  function goPage(index: number, done?: () => void): string | null {
    if (index < 0 || index >= pages.length) return `ไม่มีหน้า ${index + 1}`;
    if (index === cur) { done?.(); return null; }
    const why = busy(); if (why) return why;
    flush();
    readCanvas();
    showPage(index, done);
    return null;
  }

  /** set properties on objects anywhere in the design (one undo step); returns how many were found */
  function setOnPages(items: Array<{ page: number; index: number }>, props: Record<string, unknown>): number {
    if (locked) return 0;
    flush();
    readCanvas();
    let done = 0;
    const byPage = new Map<number, number[]>();
    for (const it of items) byPage.set(it.page, [...(byPage.get(it.page) ?? []), it.index]);
    for (const [page, indexes] of byPage) {
      if (page === cur) {
        const objs = ed.userObjects();
        for (const k of indexes) if (objs[k]) { objs[k].set(props as Partial<typeof objs[number]>); done++; }
      } else if (pages[page] !== undefined) {
        const objs = JSON.parse(pages[page]) as Array<Record<string, unknown>>;
        for (const k of indexes) if (objs[k]) { Object.assign(objs[k], props); done++; }
        pages[page] = JSON.stringify(objs);
      }
    }
    ed.canvas.requestRenderAll();
    if (done) record();
    return done;
  }

  /**
   * เอกสาร (src/ui/docFill.ts): every page changed at once as saved JSON — fill the fields, switch the language — one
   * undo step; the open page is drawn again from its new JSON. `fn` returns the new pages, or a Thai reason (then
   * nothing changes). Returns a Thai reason when it cannot be done now.
   */
  function rewritePages(fn: (pages: Array<Array<Record<string, unknown>>>) => Array<Array<Record<string, unknown>>> | string): string | null {
    const why = busy(); if (why) return why;
    flush();
    readCanvas();
    const after = fn(pages.map(p => JSON.parse(p) as Array<Record<string, unknown>>));
    if (typeof after === 'string') return after;
    if (after.length !== pages.length) return 'จำนวนหน้าไม่ตรงกับงาน ไม่ได้เปลี่ยนอะไร ลองใหม่';
    pages = after.map(p => JSON.stringify(p));
    showPage(cur, record);
    return null;
  }

  /** replace the open video project (one undo step); returns a Thai reason when it cannot be done now */
  function setVideo(p: VideoProject): string | null {
    if (locked) return 'กำลังเปิดงานอยู่ รอสักครู่แล้วลองใหม่';
    if (!ed.doc.video) return 'งานที่เปิดอยู่ไม่ใช่งานวิดีโอ';
    if (p.v !== 4) return 'ข้อมูลวิดีโอเป็นรุ่นเก่า กรุณารีเฟรชหน้าเว็บ';
    // the same check the server and the next load make: an edit that fails it would leave a work that cannot be opened
    const c = checkVideo(p);
    if (!c.ok) return 'แก้แบบนี้ไม่ได้: ' + c.error;
    flush();
    ed.doc.video = p;
    record();
    return null;
  }

  /** every undo/redo step of the open work as JSON text (to keep the clips undo may bring back) */
  const steps = (): string[] => stack.map(s => s.json);

  /** each page's objects as plain data (for the export check list) */
  function allPageObjects<T>(): T[][] {
    readCanvas();
    return pages.map(p => JSON.parse(p) as T[]);
  }

  /**
   * a blank page (or a copy of page `copyOf`) right after `after`; opens it.
   * Its size: `size` when given (ขนาดหน้าใหม่), else the copied page's, else the open page's; the bleed comes along.
   */
  function addPage(after: number, copyOf?: number, size?: PageSize): string | null {
    const why = busy(); if (why) return why;
    if (pages.length >= MAX_PAGES) return `งานหนึ่งมีได้สูงสุด ${MAX_PAGES} หน้า`;
    if (size && ed.doc.video) return 'งานวิดีโอมีขนาดเดียว เพิ่มหน้าขนาดอื่นไม่ได้';
    flush();
    readCanvas();
    pages.splice(after + 1, 0, copyOf === undefined ? '[]' : pages[copyOf]);
    sizes.splice(after + 1, 0, { ...(size ?? sizes[copyOf ?? cur]) });
    names.splice(after + 1, 0, copyOf === undefined ? '' : names[copyOf]);   // a copy keeps the name
    guides.splice(after + 1, 0, copyOf === undefined ? noGuides() : copyGuides(guides[copyOf]));   // …and its guides
    showPage(after + 1, record);
    return null;
  }

  /**
   * เทมเพลต → "แทนหน้านี้" / "เพิ่มเป็นหน้าใหม่" (26 ต.ค. 2569): template pages put into the open work, each with its
   * own size (choice ก: the page takes the template's size), as one undo step; the first of them is opened.
   *   replace  the open page becomes the first (its name and bleed stay), the others come right after it
   *   add      all of them come right after the open page
   * Returns a Thai reason when it cannot be done.
   */
  function putPages(list: Array<{ objects: string; size: PageSize }>, mode: 'replace' | 'add'): string | null {
    const why = busy(); if (why) return why;
    if (ed.doc.video) return 'งานที่เปิดอยู่เป็นงานวิดีโอ ใส่เทมเพลตงานออกแบบลงไปไม่ได้ เลือก "เปิดเป็นงานใหม่" แทน';
    if (!list.length) return 'เทมเพลตนี้ไม่มีหน้า';
    const total = pages.length + list.length - (mode === 'replace' ? 1 : 0);
    if (total > MAX_PAGES) return `งานหนึ่งมีได้สูงสุด ${MAX_PAGES} หน้า (ใส่แล้วจะเป็น ${total} หน้า) เลือก "เปิดเป็นงานใหม่" แทน`;
    for (const [k, p] of list.entries()) {
      const bad = pageSizeProblem(p.size, `ขนาดหน้า ${k + 1} ของเทมเพลต `);
      if (bad) return bad;
      try { if (!Array.isArray(JSON.parse(p.objects))) return 'เนื้อหาเทมเพลตเสีย อ่านไม่ได้'; } catch { return 'เนื้อหาเทมเพลตเสีย อ่านไม่ได้'; }
    }
    flush();
    readCanvas();
    const at = mode === 'replace' ? cur : cur + 1;
    const size = (s: PageSize, keep?: PageSize): PageSize => ({ w: Math.round(s.w), h: Math.round(s.h), bleedMm: keep ? keep.bleedMm : s.bleedMm });
    if (mode === 'replace') {
      const keep = sizes[cur];
      pages.splice(cur, 1, list[0].objects);
      sizes.splice(cur, 1, size(list[0].size, keep));
      guides.splice(cur, 1, noGuides());   // the old page's guides were for the old layout
    }
    const rest = mode === 'replace' ? list.slice(1) : list;
    const from = mode === 'replace' ? cur + 1 : at;
    pages.splice(from, 0, ...rest.map(p => p.objects));
    sizes.splice(from, 0, ...rest.map(p => size(p.size)));
    names.splice(from, 0, ...rest.map(() => ''));
    guides.splice(from, 0, ...rest.map(() => noGuides()));
    showPage(at, record);
    return null;
  }

  /**
   * new size and/or bleed for the open page or every page (one undo step); objects stay where they are.
   * Returns a Thai reason when it cannot be done now.
   */
  function resizePages(which: 'this' | 'all', change: { w?: number; h?: number; bleedMm?: number; bleedIfNone?: number }): string | null {
    const why = busy(); if (why) return why;
    if (which === 'all' && ed.doc.video && pages.length > 1) return 'งานวิดีโอมีได้หน้าเดียว';
    flush();
    readCanvas();
    const at = which === 'all' ? sizes.map((_, k) => k) : [cur];
    for (const k of at) {
      if (change.w !== undefined) sizes[k] = { ...sizes[k], w: Math.round(change.w) };
      if (change.h !== undefined) sizes[k] = { ...sizes[k], h: Math.round(change.h) };
      if ('bleedMm' in change) sizes[k] = { ...sizes[k], bleedMm: change.bleedMm };
      // a print size (mm/cm) on a page without bleed gets the standard one; pages that have a bleed keep theirs
      if (change.bleedIfNone !== undefined && !((sizes[k].bleedMm ?? 0) > 0)) sizes[k] = { ...sizes[k], bleedMm: change.bleedIfNone };
    }
    applySize(cur);
    record();
    return null;
  }

  /** the print bleed of the listed pages (the check list's "ตั้งระยะตัดตก"); one undo step */
  function setBleedOn(list: number[], mm: number): string | null {
    const why = busy(); if (why) return why;
    flush();
    readCanvas();
    for (const k of list) if (sizes[k]) sizes[k] = { ...sizes[k], bleedMm: mm };
    applySize(cur);
    record();
    return null;
  }

  function deletePage(index: number): string | null {
    const why = busy(); if (why) return why;
    if (pages.length <= 1) return 'งานต้องมีอย่างน้อย 1 หน้า ใช้ "เริ่มงานใหม่" ในเมนูเพิ่มเติมถ้าต้องการล้างหน้างาน';
    flush();
    readCanvas();
    pages.splice(index, 1);
    sizes.splice(index, 1);
    names.splice(index, 1);
    guides.splice(index, 1);
    showPage(Math.min(index, pages.length - 1), record);
    return null;
  }

  /** move page `index` one place earlier (-1) or later (+1); the page on the canvas stays the same page */
  function movePage(index: number, dir: -1 | 1): string | null {
    const to = index + dir;
    if (to < 0 || to >= pages.length) return dir < 0 ? 'หน้านี้อยู่หน้าแรกแล้ว' : 'หน้านี้อยู่หน้าสุดท้ายแล้ว';
    return movePageTo(index, to);
  }

  /** move page `from` to place `to` (ลากเรียงหน้า); the page on the canvas stays the same page; one undo step */
  function movePageTo(from: number, to: number): string | null {
    const why = busy(); if (why) return why;
    if (from < 0 || from >= pages.length || to < 0 || to >= pages.length) return `ไม่มีหน้า ${Math.max(from, to) + 1}`;
    if (from === to) return null;
    flush();
    readCanvas();
    const move = <T>(a: T[]) => { const [x] = a.splice(from, 1); a.splice(to, 0, x); };
    move(pages); move(sizes); move(names); move(guides);
    cur = movedIndex(cur, from, to);
    record();
    hooks.onRestored();
    return null;
  }

  /** name page `index` ('' = no name); one undo step; a Thai reason when it cannot be used */
  function setPageName(index: number, raw: string): string | null {
    const why = busy(); if (why) return why;
    if (index < 0 || index >= pages.length) return `ไม่มีหน้า ${index + 1}`;
    const name = cleanPageName(raw);
    if ([...name].length > MAX_PAGE_NAME) return `ชื่อหน้ายาวเกิน ${MAX_PAGE_NAME} ตัวอักษร (ตอนนี้ ${[...name].length})`;
    if (name === names[index]) return null;   // nothing changes (also: clearing a name never set)
    flush();
    names[index] = name;
    namesKnown = true;   // from now on this app writes the names (and the server keeps them safe from older apps)
    record();
    return null;
  }

  /**
   * the open page's guides (เส้นไกด์: added, moved or removed on the rulers); one undo step.
   * A Thai reason when it cannot be done now or the guides cannot be kept (the same check as opening and the server).
   */
  function setPageGuides(g: PageGuides): string | null {
    const why = busy(); if (why) return why;
    const next = guides.map((x, k) => k === cur ? copyGuides(g) : x);
    const bad = guidesProblem(next, pages.length);
    if (bad) return bad;
    flush();
    guides = next;
    guidesKnown = true;   // from now on this app writes the guides (and the server keeps them safe from older apps)
    ed.setPageGuides(guides[cur]);
    record();
    return null;
  }

  /** remove every guide on the open page (`all`: on every page); one undo step; how many were removed */
  function clearGuides(all: boolean): number {
    if (busy()) return 0;
    const at = all ? guides.map((_, k) => k) : [cur];
    const n = at.reduce((t, k) => t + guides[k].x.length + guides[k].y.length, 0);
    if (!n) return 0;
    flush();
    guides = guides.map((g, k) => at.includes(k) ? noGuides() : g);
    guidesKnown = true;
    ed.setPageGuides(guides[cur]);
    record();
    return n;
  }

  /** back to one empty page ("เริ่มงานใหม่"); undoable */
  function clearAll(): string | null {
    const why = busy(); if (why) return why;
    flush();
    readCanvas();
    pages = ['[]'];
    sizes = [sizes[0]];
    names = [''];
    guides = [noGuides()];
    showPage(0, record);
    return null;
  }

  /** a page as plain Fabric JSON (for its preview picture); the page on the canvas is read fresh */
  function pageJson(index: number): string {
    if (index === cur) readCanvas();
    return `{"version":${JSON.stringify(meta.version ?? '5.3.0')},"objects":${pages[index] ?? '[]'}}`;
  }

  /** blob: picture addresses still used on pages that are not on the canvas (not uploaded yet) */
  function blobUrlsOffCanvas(): string[] {
    const found = new Set<string>();
    pages.forEach((p, k) => { if (k !== cur) for (const m of p.matchAll(/"src":"(blob:[^"]+)"/g)) found.add(m[1]); });
    return [...found];
  }

  /** after uploading a picture: point the pages that are not on the canvas at its server address */
  function replaceOffCanvas(from: string, to: string) {
    pages = pages.map((p, k) => k === cur ? p : p.split(JSON.stringify(from)).join(JSON.stringify(to)));
  }

  return {
    push, load, reset, silently, snapshot,
    undo: () => go(-1),
    redo: () => go(1),
    canUndo: () => i > 0,
    canRedo: () => i < stack.length - 1,
    /* pages */
    pageCount: () => pages.length,
    currentPage: () => cur,
    goPage, addPage, putPages, rewritePages, deletePage, movePage, movePageTo, setPageName, clearAll,
    /* guides (src/ui/rulers.ts) */
    setPageGuides, clearGuides,
    /** the open page's guides (a copy) */
    pageGuides: (): PageGuides => copyGuides(guides[cur]),
    /** how many guides the design has on all its pages */
    guideCount: () => guides.reduce((t, g) => t + g.x.length + g.y.length, 0),
    /** page `k`'s name ('' = none) */
    pageName: (k: number) => names[k] ?? '', pageJson, blobUrlsOffCanvas, replaceOffCanvas, setOnPages, allPageObjects,
    resizePages, setBleedOn,
    /** record an edit still waiting in its 250 ms pause now (before an action that is one undo step of its own) */
    flushEdit: () => flush(),
    /** page `k`'s size and bleed (the open page read fresh from the editor) */
    pageSize: (k: number): PageSize => ({ ...(k === cur && !loading ? sizeNow() : sizes[k]) }),
    pageSizes: (): PageSize[] => sizes.map((s, k) => ({ ...(k === cur && !loading ? sizeNow() : s) })),
    /** the pages are not all the same size and bleed */
    mixed: () => mixedSizes(sizes.map((s, k) => k === cur && !loading ? sizeNow() : s)),
    /* video project */
    setVideo, steps,
    /** a page is being put on the canvas (objects appearing now are the saved ones, not new ones) */
    get loading() { return loading; }
  };
}

export type History = ReturnType<typeof createHistory>;
